import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const iconTileVariants = cva(
  "inline-flex shrink-0 items-center justify-center bg-fill-2 text-fg-2 [&_svg]:shrink-0",
  {
    variants: {
      size: {
        sm: "size-5 rounded-sm text-xs [&_svg]:size-3",
        md: "size-6 rounded-md text-xs [&_svg]:size-3.5",
        lg: "size-8 rounded-lg text-sm [&_svg]:size-4",
        xl: "size-12 rounded-xl text-lg font-medium [&_svg]:size-5"
      }
    },
    defaultVariants: { size: "md" }
  }
);

type IconTileProps = React.ComponentProps<"span"> &
  VariantProps<typeof iconTileVariants> & {
    icon?: React.ComponentType<{ className?: string }>;
  };

/**
 * A neutral `fill-2` tile holding an icon, logo or initials: 24px in list
 * rows, 32px in empty states, 48px on overview pages.
 */
function IconTile({
  icon: Icon,
  size,
  className,
  children,
  ...props
}: IconTileProps) {
  return (
    <span
      data-slot="icon-tile"
      className={cn(iconTileVariants({ size }), className)}
      aria-hidden={Icon && !children ? true : undefined}
      {...props}
    >
      {Icon ? <Icon /> : children}
    </span>
  );
}

export { IconTile, iconTileVariants };
export type { IconTileProps };
